import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { isTesting, macroCondition } from '@embroider/macros';
import {
  didCancel,
  type Task,
  task,
  type TaskInstance,
} from 'ember-concurrency';
import type { Hotel } from 'frontend-utils/models';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type { PaRatePreferenceOption } from 'lumen/models';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';

interface PreferredRateOnboardingModel {
  ratePreferenceTaskResult: TaskInstance<void>;
  requestPricingAssistantRatePreferencesOptionsTaskResult: TaskInstance<PaRatePreferenceOption | null>;
  saveAndUpdatePreferredRateSettings: Task<{ isSuccess: boolean }, [unknown]>;
}

export default class HotelWizardsPreferredRateOnboardingRoute extends Route {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @service declare intl: Services['intl'];
  @service('pricing-assistant-data-request-service')
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  @service declare pricingAssistantPmsSs: Services['pricing-assistant-pms-ss'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  requestPricingAssistantRatePreferencesOptionsTask = task(
    { restartable: true },
    async (hotelId) => {
      try {
        const apiResponse =
          await this.store.queryRecord<PaRatePreferenceOption>(
            'pa-rate-preference-option',
            {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              subscription_id: hotelId,
              namespace: APIGATEWAY,
              typeKey: 'pricing-assistant/settings/rate_preferences/options',
            },
          );
        return apiResponse;
      } catch (error) {
        this.basicErrorHandling(error);
      }

      return null;
    },
  );

  saveAndUpdatePreferredRateSettingsTask = task(
    { drop: true },
    async (preferredRateSettings) => {
      let apiResponse = null;

      try {
        const standardParams = {
          subscription_id: this.hotelId,
          // boolean allowing BE to know that this is an onboarding request
          // and they should not lock edit rights for the client
          is_onboarding: true,
        };

        const requestUrl = formatUrl(
          '/apigateway/v1/app/pricing-assistant/settings/rate_preferences',
          // @ts-expect-error: Argument of type '{ subscription_id: string | null; is_onboarding: boolean; }' is not assignable to parameter of type 'FormatUrlParams'.
          standardParams,
          this.ajax.host,
        );

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        apiResponse = await this.ajax.put(requestUrl, {
          data: {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            pa_rate_preference: preferredRateSettings,
          },
          contentType: 'application/json',
        });

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.hotelAndMarketService.hotel.paRatePreference =
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          apiResponse.pa_rate_preference;

        // Updating selected channel can update the status of
        // recommendationsChannels (say we request a supported channel), so we
        // re-request the hotel resource to have the latest channel statuses.
        await Promise.all([
          this.store.query<Hotel>('hotel', {
            id: this.hotelAndMarketService.hotel.id,
          }),
          // The refetch also replaces the `last_edited` written above: an
          // `is_onboarding` PUT deliberately answers with a backdated one so the
          // client is not locked out, and that value must not stick on the record.
          this.refreshPaRatePreference(),
        ]);

        return {
          isSuccess: true,
        };
      } catch (error) {
        this.basicErrorHandling(error);
        return {
          isSuccess: false,
        };
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelId() {
    return this.hotelAndMarketService.hotel?.id;
  }

  basicErrorHandling(error: unknown): void {
    if (error) {
      this.errorTracking.captureException(error);
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.alerting.showErrorAlert({
      text: getAPIErrorMessage(error, this.intl),
    });
  }

  beforeModel(transition: Transition): void {
    if (
      this.pricingAssistantPmsSs.isPricingAssistantPMSIntegrated ||
      !this.access.hasPricingAssistantSettingsEditRights ||
      !this.access.hasPricingAssistantSettingsPreferredRateTab
    ) {
      if (this.access.hasPricingAssistantTab) {
        this.router.transitionTo('hotel.pricing-manager');
      } else {
        this.transition.redirectToEnabledTab(transition);
      }
    } else if (macroCondition(!isTesting())) {
      this.router.transitionTo(
        'hotel.wizards.preferred-rate-onboarding.rate-type',
      );
    }
  }

  model(): PreferredRateOnboardingModel {
    // The wizard service snapshots the preference in its constructor, first
    // touched at render, so the step templates gate on this task until it lands.
    const ratePreferenceTaskResult =
      this.pricingAssistantDataRequestService.loadPaRatePreferenceTask.perform(
        this.hotelId!,
      );

    const model = {
      ratePreferenceTaskResult,
      requestPricingAssistantRatePreferencesOptionsTaskResult:
        this.requestPricingAssistantRatePreferencesOptionsTask.perform(
          this.hotelId,
        ),
      saveAndUpdatePreferredRateSettings:
        this.saveAndUpdatePreferredRateSettingsTask,
    };

    return model;
  }

  async refreshPaRatePreference(): Promise<void> {
    try {
      await this.pricingAssistantDataRequestService.requestPaRatePreferenceTask.perform(
        this.hotelId!,
      );
    } catch (e) {
      // The save itself succeeded and reports that, so neither a failed
      // request nor the cancellation from leaving the wizard mid-save (the
      // task is restartable) may surface as a save failure.
      if (!didCancel(e)) {
        this.errorTracking.addBreadcrumb({
          message:
            'Pricing Assistant rate preference refresh after an onboarding save failed',
        });
        this.errorTracking.captureException(e);
      }
    }
  }
}
